import React, { useState, useRef, useCallback, useMemo, useEffect, type DragEvent } from 'react';
import { Capacitor } from '@capacitor/core';
import { openStripeCheckout } from '@/utils/stripeCheckout';
import { getPurchaseProvider } from '@/utils/purchaseAvailability';
import { purchasePremium } from '@/utils/playBilling';
import { captureError } from '@/utils/captureError';
import { ArrowRight, Upload, CheckCircle, ChevronDown } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import {
  Button,
  Heading,
  Body,
  MonoLabel,
  Card,
  Spinner,
  InlineMessage,
  showToast,
  WineTypeIndicator,
} from '@/components/rc';
import type { WineType } from '@/components/rc/WineTypeIndicator';
import { authFetch } from '@/utils/authFetch';
import { FUNCTION_URLS } from '@/config/functionUrls';
import { useProfile } from '@/context/ProfileContext';
import { useInventory } from '@/context/InventoryContext';
import { trackEvent } from '@/config/analytics';
import { CONFIG } from '@/constants';
import { cn } from '@/lib/utils';
import { getCurrencySymbol } from '@/lib/currencyConversion';
import {
  detectDuplicates,
  buildMergedCSVWithNotes,
  buildFilteredCSV,
  parseAllWines,
  mappingFingerprint,
  type DuplicateGroup,
  type ParsedWine,
} from '@/utils/importDedup';
import { batchEnrichWines, type ImportedWineData } from '@/services/enrichmentService';
import DuplicateReview from './DuplicateReview';
import BottleSelectGate from './BottleSelectGate';

// ── Types ──

const IMPORTABLE_FIELDS = [
  'producer', 'name', 'vintage', 'type', 'cepage', 'region', 'country',
  'appellation', 'location', 'quantity', 'drinkFrom', 'drinkUntil', 'price', 'priceCurrency',
  'format', 'tastingNotes', 'personalNote', 'myRating', 'criticRating', 'linkToWine',
  'imageUrl',
] as const;

type ImportableField = typeof IMPORTABLE_FIELDS[number];

interface FieldMapping {
  csvColumn: string;
  raveCaveField: ImportableField | null;
  confidence: 'high' | 'medium' | 'low';
  sampleValues: string[];
}

interface MappingResult {
  mappings: FieldMapping[];
  detectedSource: string;
  notes: string;
  totalRows: number;
  parseWarnings: string[];
}

type ImportStage =
  | 'upload'
  | 'mapping-loading'
  | 'mapping'
  | 'dedup-review'
  | 'bottle-select'
  | 'preview'
  | 'importing'
  | 'complete';

export const IMPORT_PENDING_LS_KEY = 'rave-cave-import-pending';
const IMPORT_PENDING_TTL_MS = 15 * 60 * 1000; // 15 minutes

const FIELD_LABELS: Record<string, string> = {
  producer: 'Producer',
  name: 'Wine Name',
  vintage: 'Vintage',
  type: 'Wine Type',
  cepage: 'Grape / Cépage',
  region: 'Region',
  country: 'Country',
  appellation: 'Appellation',
  quantity: 'Quantity',
  drinkFrom: 'Drink From',
  drinkUntil: 'Drink Until',
  price: 'Price',
  priceCurrency: 'Price Currency',
  format: 'Format',
  tastingNotes: 'Tasting Notes',
  personalNote: 'Personal Note',
  myRating: 'My Rating',
  criticRating: 'Critic Rating',
  linkToWine: 'Link to Wine',
  imageUrl: 'Label Image',
};

// ── Component ──

interface ImportFlowProps {
  onClose: () => void;
}

export default function ImportFlow({ onClose }: ImportFlowProps) {
  const navigate = useNavigate();
  const { isPremium, profile } = useProfile();
  const { totalBottles } = useInventory();

  // Read inside handleBottleSelectUpgrade's Play branch to detect the
  // webhook landing, without retriggering that useCallback on every
  // profile change.
  const isPremiumRef = useRef(isPremium);
  isPremiumRef.current = isPremium;

  const [stage, setStage] = useState<ImportStage>('upload');
  const [confirmingPurchase, setConfirmingPurchase] = useState(false);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [mappingResult, setMappingResult] = useState<MappingResult | null>(null);
  const [confirmedMappings, setConfirmedMappings] = useState<FieldMapping[]>([]);
  const [importProgress, setImportProgress] = useState({
    total: 0, imported: 0, skipped: 0, duplicates: 0,
  });
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [duplicateGroups, setDuplicateGroups] = useState<DuplicateGroup[]>([]);
  const [mergedCSV, setMergedCSV] = useState<string | null>(null);
  const [importMappings, setImportMappings] = useState<{ csvColumn: string; raveCaveField: string | null }[] | null>(null);
  const lastMappingFP = useRef<string>('');

  // ── Bottle-select state ──
  const [parsedWines, setParsedWines] = useState<ParsedWine[]>([]);
  const [filteredCSV, setFilteredCSV] = useState<string | null>(null);
  const [passedThroughBottleSelect, setPassedThroughBottleSelect] = useState(false);

  // ── Tier cap (bottle-aware) ──
  const remainingCapacity = isPremium
    ? Infinity
    : Math.max(0, CONFIG.FREE_TIER.MAX_BOTTLES - totalBottles);

  // Effective CSV is the final CSV after dedup + optional bottle selection
  const effectiveCSV = filteredCSV ?? mergedCSV ?? csvContent;

  // Count actual bottles from parsed wines (sum of quantities)
  const csvBottleCount = useMemo(() => {
    if (parsedWines.length > 0) return parsedWines.reduce((sum, w) => sum + w.quantity, 0);
    return mappingResult?.totalRows ?? 0;
  }, [parsedWines, mappingResult]);

  // Row count of effective CSV (for maxWines to backend, which caps by rows)
  const effectiveRowCount = useMemo(() => {
    if (!effectiveCSV) return 0;
    const lines = effectiveCSV.split('\n');
    let count = 0;
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) count++;
    }
    return count;
  }, [effectiveCSV]);

  // ── Restore from localStorage (post-upgrade resume) ──
  useEffect(() => {
    try {
      const raw = localStorage.getItem(IMPORT_PENDING_LS_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (Date.now() - data.savedAt > IMPORT_PENDING_TTL_MS) {
        localStorage.removeItem(IMPORT_PENDING_LS_KEY);
        return;
      }
      // Restore state
      setCsvContent(data.csvContent);
      setFileName(data.fileName);
      setConfirmedMappings(data.confirmedMappings);
      setMappingResult(data.mappingResult);
      if (data.duplicateGroups?.length > 0 && data.csvContent) {
        setDuplicateGroups(data.duplicateGroups);
        const noteResult = buildMergedCSVWithNotes(data.csvContent, data.duplicateGroups, data.confirmedMappings);
        setMergedCSV(noteResult.csv);
        setImportMappings(noteResult.mappings);
      } else if (data.csvContent) {
        const noteResult = buildMergedCSVWithNotes(data.csvContent, [], data.confirmedMappings);
        setMergedCSV(noteResult.csv);
        setImportMappings(noteResult.mappings);
      }
      if (data.lastMappingFP) lastMappingFP.current = data.lastMappingFP;
      // User is now premium (or will be shortly) — skip to preview
      setStage('preview');
      localStorage.removeItem(IMPORT_PENDING_LS_KEY);
    } catch {
      localStorage.removeItem(IMPORT_PENDING_LS_KEY);
    }
  }, []);

  // ── Mapping validation ──
  const hasRequiredMapping = useMemo(() => {
    return confirmedMappings.some(
      m => m.raveCaveField === 'name' || m.raveCaveField === 'producer'
    );
  }, [confirmedMappings]);

  const missingWarnings = useMemo(() => {
    const warnings: string[] = [];
    if (!confirmedMappings.some(m => m.raveCaveField === 'vintage')) {
      warnings.push('Vintage not mapped — all wines will import as NV');
    }
    if (!confirmedMappings.some(m => m.raveCaveField === 'quantity')) {
      warnings.push('Quantity not mapped — defaulting to 1 bottle each');
    }
    return warnings;
  }, [confirmedMappings]);

  // ── Used fields (for disabling duplicate mapping in dropdowns) ──
  const usedFields = useMemo(() => {
    const set = new Set<string>();
    for (const m of confirmedMappings) {
      if (m.raveCaveField) set.add(m.raveCaveField);
    }
    return set;
  }, [confirmedMappings]);

  // ── File handling ──
  const handleFile = useCallback(async (file: File) => {
    setError(null);

    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum 5MB.');
      return;
    }

    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['csv', 'tsv', 'txt'].includes(ext || '')) {
      setError('Unsupported file type. Please upload a .csv, .tsv, or .txt file.');
      return;
    }

    setFileName(file.name);
    const text = await file.text();
    setCsvContent(text);
    setStage('mapping-loading');

    try {
      const res = await authFetch(FUNCTION_URLS.mapImportFields, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv: text }),
      });
      const data = await res.json();

      if (data.error) {
        setError(data.error);
        setStage('upload');
        return;
      }

      setMappingResult(data);
      setConfirmedMappings(data.mappings);
      setStage('mapping');
    } catch (e: any) {
      setError(e.message || 'Failed to analyse CSV. Please try again.');
      setStage('upload');
    }
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  // ── Mapping change ──
  const handleMappingChange = useCallback((index: number, field: ImportableField | null) => {
    setConfirmedMappings(prev => prev.map((m, i) =>
      i === index ? { ...m, raveCaveField: field, confidence: 'high' } : m
    ));
  }, []);

  // ── Preview data ──
  const previewWines = useMemo(() => {
    const csv = effectiveCSV;
    const activeMappings = importMappings ?? confirmedMappings;
    if (!csv || !activeMappings.length) return [];

    // Quick client-side parse for preview
    const lines = csv.split('\n');
    if (lines.length < 2) return [];

    const headers = parseCSVLine(lines[0]);
    const wines: Record<string, string>[] = [];

    for (let i = 1; i < Math.min(lines.length, 6); i++) {
      if (!lines[i].trim()) continue;
      const values = parseCSVLine(lines[i]);
      const wine: Record<string, string> = {};
      for (const m of activeMappings) {
        if (!m.raveCaveField) continue;
        const colIndex = headers.findIndex(h => h.trim() === m.csvColumn);
        if (colIndex >= 0 && values[colIndex]) {
          wine[m.raveCaveField] = values[colIndex].trim();
        }
      }
      if (wine.producer || wine.name) wines.push(wine);
    }

    return wines;
  }, [effectiveCSV, confirmedMappings, importMappings]);

  // ── Save import state to localStorage (for Stripe upgrade-and-return) ──
  const saveImportState = useCallback(() => {
    try {
      localStorage.setItem(IMPORT_PENDING_LS_KEY, JSON.stringify({
        csvContent,
        fileName,
        confirmedMappings,
        mappingResult,
        duplicateGroups,
        lastMappingFP: lastMappingFP.current,
        savedAt: Date.now(),
      }));
    } catch {
      // QuotaExceededError for large CSVs
      showToast({ message: 'File too large to preserve. Please re-upload after upgrading.', tone: 'error' });
    }
  }, [csvContent, fileName, confirmedMappings, mappingResult, duplicateGroups]);

  // ── Bottle-select confirm — shared by "Continue with N" and a
  // successful in-app purchase, which effectively selects everything ──
  const handleBottleSelectConfirm = useCallback((selectedWines: ParsedWine[]) => {
    const selectedLineIndices = selectedWines.map(w => w.lineIndex);
    const sourceCSV = mergedCSV ?? csvContent;
    if (sourceCSV) {
      setFilteredCSV(buildFilteredCSV(sourceCSV, selectedLineIndices));
    }
    setPassedThroughBottleSelect(true);
    setStage('preview');
  }, [mergedCSV, csvContent]);

  // ── Bottle-select → upgrade handler ──
  const handleBottleSelectUpgrade = useCallback(async () => {
    const provider = getPurchaseProvider();
    trackEvent('upgrade_initiated', { feature: 'import_bottles' });
    // Clear any previous failure before retrying, as handleFile does —
    // otherwise a stale error sits next to "Confirming..." on a second go.
    setError(null);

    if (provider === 'play') {
      setConfirmingPurchase(true);
      try {
        const outcome = await purchasePremium();
        if (outcome.status === 'error') {
          setError(outcome.message);
          return;
        }
        if (outcome.status === 'cancelled') return;

        // The purchase is confirmed with Google immediately, but our tier
        // write happens server-side once the RevenueCat webhook lands —
        // typically a couple of seconds, not instant. Unlike the Stripe
        // path (which leaves the app and only resumes once the reactive
        // isPremium listener has already flipped, via the router's
        // IMPORT_PENDING_LS_KEY check), nothing here naturally waits for
        // that, so poll briefly rather than importing against a still-free
        // capacity and truncating a purchase the user just paid for.
        const start = Date.now();
        while (!isPremiumRef.current && Date.now() - start < 8000) {
          await new Promise(resolve => setTimeout(resolve, 400));
        }
        if (!isPremiumRef.current) {
          setError("Purchase confirmed, but it's taking a moment to apply. Please try importing again in a few seconds.");
          return;
        }
        handleBottleSelectConfirm(parsedWines);
      } finally {
        setConfirmingPurchase(false);
      }
      return;
    }

    // Stripe: persist state — see saveImportState — then redirect out.
    saveImportState();
    try {
      const res = await authFetch(FUNCTION_URLS.createCheckout, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to start checkout');
      }
      const { url } = await res.json();
      if (url) {
        await openStripeCheckout(url);
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (e: any) {
      setError(e.message || 'Something went wrong. Please try again.');
    }
  }, [saveImportState, parsedWines, handleBottleSelectConfirm]);

  // ── Import handler ──
  const handleImport = useCallback(async () => {
    setStage('importing');
    setError(null);
    setImportProgress({ total: effectiveRowCount, imported: 0, skipped: 0, duplicates: 0 });

    const activeMappings = importMappings ?? confirmedMappings;

    try {
      const res = await authFetch(FUNCTION_URLS.commitImport, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          csv: effectiveCSV,
          mappings: activeMappings.map(m => ({
            csvColumn: m.csvColumn,
            raveCaveField: m.raveCaveField,
          })),
          maxWines: effectiveRowCount,
          defaultCurrency: profile.currency,
        }),
      });

      const result = await res.json();

      if (result.error) {
        setError(result.error);
        setStage('preview');
        return;
      }

      setImportProgress({
        total: effectiveRowCount,
        imported: result.imported,
        skipped: result.skipped,
        duplicates: result.duplicatesMerged,
      });
      setStage('complete');

      trackEvent('wines_imported', {
        count: result.imported,
        duplicates: result.duplicatesMerged,
        skipped: result.skipped,
        source: mappingResult?.detectedSource || 'unknown',
      });

      // Post-import enrichment: if tasting notes, drink from, or drink to are unmapped
      const needsEnrichment =
        !confirmedMappings.some(m => m.raveCaveField === 'tastingNotes') ||
        !confirmedMappings.some(m => m.raveCaveField === 'drinkFrom') ||
        !confirmedMappings.some(m => m.raveCaveField === 'drinkUntil');

      const createdWines = result.createdWines as ImportedWineData[] | undefined;
      if (needsEnrichment && createdWines && createdWines.length > 0) {
        showToast({ message: 'Remy is enriching your wines...' });
        batchEnrichWines(createdWines).catch(err => captureError(err, { context: 'import' }));
      }
    } catch (e: any) {
      setError(e.message || 'Import failed. Please try again.');
      setStage('preview');
    }
  }, [effectiveRowCount, effectiveCSV, confirmedMappings, importMappings, mappingResult]);

  // ── Source label ──
  const sourceLabel = mappingResult?.detectedSource === 'cellartracker'
    ? 'CellarTracker'
    : mappingResult?.detectedSource === 'vivino'
      ? 'Vivino'
      : 'CSV';

  return (
    <Dialog open onOpenChange={(open) => {
      if (!open) {
        localStorage.removeItem(IMPORT_PENDING_LS_KEY);
        onClose();
      }
    }}>
      <DialogContent
        className="!block max-w-[640px] max-h-[90dvh] overflow-y-auto overflow-x-hidden bg-[var(--rc-surface-primary)] p-0"
        onPointerDownOutside={(e) => {
          if (stage === 'importing') e.preventDefault();
        }}
      >
        <DialogTitle className="sr-only">Import Wines</DialogTitle>

        <div className="p-6">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              {stage !== 'upload' && stage !== 'mapping-loading' && stage !== 'importing' && stage !== 'complete' && (
                <button
                  onClick={() => {
                    if (stage === 'mapping') setStage('upload');
                    if (stage === 'dedup-review') {
                      setMergedCSV(null);
                      setImportMappings(null);
                      setStage('mapping');
                    }
                    if (stage === 'bottle-select') {
                      setParsedWines([]);
                      setMergedCSV(null);
                      setImportMappings(null);
                      setStage(duplicateGroups.length > 0 ? 'dedup-review' : 'mapping');
                    }
                    if (stage === 'preview') {
                      if (passedThroughBottleSelect) {
                        setFilteredCSV(null);
                        setStage('bottle-select');
                      } else {
                        setMergedCSV(null);
                        setImportMappings(null);
                        setStage(duplicateGroups.length > 0 ? 'dedup-review' : 'mapping');
                      }
                    }
                  }}
                  className="text-[var(--rc-ink-ghost)] hover:text-[var(--rc-ink-primary)] transition-colors text-sm"
                >
                  &larr; Back
                </button>
              )}
              <Heading scale="heading">Import Wines</Heading>
            </div>
            {/* Filename subtitle (toned down) */}
            {fileName && stage !== 'upload' && (
              <MonoLabel size="micro" colour="ghost" className="w-auto mt-1 block">
                {fileName}
              </MonoLabel>
            )}
          </div>

          {/* Step progress indicator */}
          {stage !== 'upload' && stage !== 'complete' && (
            <ImportStepIndicator stage={stage} hasDuplicates={duplicateGroups.length > 0} />
          )}

          {/* Error */}
          {error && (
            <div className="mb-4">
              <InlineMessage tone="error" message={error} />
            </div>
          )}

          {/* ── Upload Stage ── */}
          {stage === 'upload' && (
            <div>
              <Body className="w-auto mb-2">
                Upload a CSV file exported from CellarTracker, Vivino, or any spreadsheet.
              </Body>
              <Body size="caption" colour="secondary" className="w-auto mb-6">
                Vivino users: upload <strong>cellar.csv</strong> for your current collection,
                or <strong>full_wine_list.csv</strong> for everything you've scanned.
              </Body>

              <div
                className={cn(
                  'border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-colors',
                  dragOver
                    ? 'border-[var(--rc-accent-pink)] bg-[var(--rc-accent-pink)]/5'
                    : 'border-[var(--rc-border-subtle)] hover:border-[var(--rc-ink-ghost)]'
                )}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
              >
                <Upload size={32} className="mx-auto mb-4 text-[var(--rc-ink-ghost)]" />
                <Body className="w-auto mb-1">
                  {Capacitor.isNativePlatform() ? 'Tap to choose a .csv file' : 'Drag & drop a .csv file here'}
                </Body>
                {!Capacitor.isNativePlatform() && (
                  <Body size="caption" colour="ghost" className="w-auto">or click to browse</Body>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.tsv,.txt"
                  className="hidden"
                  onChange={handleInputChange}
                />
              </div>

              <MonoLabel size="label" colour="ghost" className="mt-4 block text-center">
                Supported: .csv, .tsv, .txt (max 5MB, max 2,000 wines)
              </MonoLabel>
            </div>
          )}

          {/* ── Mapping Loading Stage ── */}
          {stage === 'mapping-loading' && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Spinner size="lg" />
              <Body className="w-auto">Analysing columns...</Body>
              <MonoLabel size="label" colour="ghost" className="w-auto">
                AI is mapping your CSV fields
              </MonoLabel>
            </div>
          )}

          {/* ── Mapping Stage ── */}
          {stage === 'mapping' && mappingResult && (
            <div>
              <Body className="w-auto mb-1">
                Detected a <strong>{sourceLabel}</strong> export with{' '}
                <strong>{mappingResult.totalRows}</strong> wines.
              </Body>
              <Body size="caption" colour="secondary" className="w-auto mb-4">
                Review the column mapping below, then continue.
              </Body>

              {mappingResult.notes && (
                <div className="mb-4">
                  <InlineMessage
                    tone={/all.*matched|matched.*automatically/i.test(mappingResult.notes) ? 'success' : 'info'}
                    message={mappingResult.notes}
                  />
                </div>
              )}

              {/* Skipped-to-notes info */}
              {confirmedMappings.some(m => !m.raveCaveField) && (
                <div className="mb-4">
                  <InlineMessage
                    tone="info"
                    message="Skipped columns won't be lost — their data will be saved to each wine's Personal Notes field."
                  />
                </div>
              )}

              {/* Mapping rows */}
              <div className="space-y-0 mb-6">
                {/* Sticky column headings */}
                <div className="sticky top-0 z-10 flex items-center gap-1.5 py-2 bg-[var(--rc-surface-primary)] border-b border-[var(--rc-border-subtle)]">
                  <div className="w-[100px] shrink-0">
                    <MonoLabel size="micro" colour="ghost" className="uppercase tracking-wider">Your Data</MonoLabel>
                  </div>
                  <div className="w-3 shrink-0" /> {/* Arrow spacer */}
                  <div className="flex-1 min-w-0">
                    <MonoLabel size="micro" colour="ghost" className="uppercase tracking-wider">Rave Cave Field</MonoLabel>
                  </div>
                  <div className="w-2 shrink-0" /> {/* Dot spacer */}
                  <div className="w-[110px] shrink-0 hidden sm:block">
                    <MonoLabel size="micro" colour="ghost" className="uppercase tracking-wider">Sample</MonoLabel>
                  </div>
                </div>

                {/* Mapped fields */}
                {confirmedMappings.map((mapping, index) =>
                  mapping.raveCaveField ? (
                    <MappingRow
                      key={mapping.csvColumn + index}
                      mapping={mapping}
                      usedFields={usedFields}
                      currentField={mapping.raveCaveField}
                      onChange={(field) => handleMappingChange(index, field)}
                      dimmed={false}
                    />
                  ) : null,
                )}

                {/* Skipped divider + fields */}
                {confirmedMappings.some(m => !m.raveCaveField) && (
                  <>
                    <div className="flex items-center gap-2 py-2 mt-1">
                      <div className="flex-1 border-t border-[var(--rc-border-subtle)]" />
                      <MonoLabel size="micro" colour="ghost" className="uppercase tracking-wider shrink-0">
                        Skipped columns
                      </MonoLabel>
                      <div className="flex-1 border-t border-[var(--rc-border-subtle)]" />
                    </div>
                    {confirmedMappings.map((mapping, index) =>
                      !mapping.raveCaveField ? (
                        <MappingRow
                          key={mapping.csvColumn + index}
                          mapping={mapping}
                          usedFields={usedFields}
                          currentField={mapping.raveCaveField}
                          onChange={(field) => handleMappingChange(index, field)}
                          dimmed={true}
                        />
                      ) : null,
                    )}
                  </>
                )}
              </div>

              {/* Warnings */}
              {missingWarnings.map((w) => (
                <div key={w} className="mb-2">
                  <InlineMessage tone="warning" message={w} />
                </div>
              ))}

              {/* Continue */}
              <div className="flex justify-end mt-4">
                <Button
                  variantType="Primary"
                  label="Continue"
                  onClick={() => {
                    if (!csvContent) return;
                    const fp = mappingFingerprint(confirmedMappings);
                    let groups = duplicateGroups;
                    // Only re-detect if mappings changed since last detection
                    if (fp !== lastMappingFP.current) {
                      groups = detectDuplicates(csvContent, confirmedMappings);
                      setDuplicateGroups(groups);
                      setMergedCSV(null);
                      setImportMappings(null);
                      setFilteredCSV(null);
                      setParsedWines([]);
                      setPassedThroughBottleSelect(false);
                      lastMappingFP.current = fp;
                    }
                    if (groups.length > 0) {
                      setStage('dedup-review');
                    } else {
                      // No duplicates — inject unmapped notes into CSV
                      const noteResult = buildMergedCSVWithNotes(csvContent, [], confirmedMappings);
                      setMergedCSV(noteResult.csv);
                      setImportMappings(noteResult.mappings);
                      // Check if bottle-select gate is needed
                      const wines = parseAllWines(noteResult.csv, noteResult.mappings);
                      setParsedWines(wines);
                      const bottleCount = wines.reduce((sum, w) => sum + w.quantity, 0);
                      if (!isPremium && bottleCount > remainingCapacity) {
                        setStage('bottle-select');
                      } else {
                        setStage('preview');
                      }
                    }
                  }}
                  disabled={!hasRequiredMapping}
                />
              </div>

              {!hasRequiredMapping && (
                <Body size="caption" colour="ghost" className="w-auto text-right mt-2">
                  Map at least Producer or Wine Name to continue
                </Body>
              )}
            </div>
          )}

          {/* ── Dedup Review Stage ── */}
          {stage === 'dedup-review' && duplicateGroups.length > 0 && (
            <DuplicateReview
              groups={duplicateGroups}
              onConfirm={(resolved) => {
                if (!csvContent) return;
                const noteResult = buildMergedCSVWithNotes(csvContent, resolved, confirmedMappings);
                setMergedCSV(noteResult.csv);
                setImportMappings(noteResult.mappings);

                // Parse wines from effective CSV to check bottle count
                const wines = parseAllWines(noteResult.csv, noteResult.mappings);
                setParsedWines(wines);
                const bottleCount = wines.reduce((sum, w) => sum + w.quantity, 0);
                if (!isPremium && bottleCount > remainingCapacity) {
                  setStage('bottle-select');
                  return;
                }
                setStage('preview');
              }}
              onToggle={(index, merged) => {
                setDuplicateGroups(prev =>
                  prev.map((g, i) => (i === index ? { ...g, merged } : g)),
                );
              }}
              onBulkMerge={() => {
                setDuplicateGroups(prev => prev.map(g => ({ ...g, merged: true })));
              }}
              onBulkKeep={() => {
                setDuplicateGroups(prev => prev.map(g => ({ ...g, merged: false })));
              }}
            />
          )}

          {/* ── Bottle Select Stage ── */}
          {stage === 'bottle-select' && parsedWines.length > 0 && (
            <BottleSelectGate
              wines={parsedWines}
              remainingCapacity={remainingCapacity === Infinity ? 0 : remainingCapacity}
              totalExistingBottles={totalBottles}
              onConfirm={handleBottleSelectConfirm}
              onUpgrade={handleBottleSelectUpgrade}
              upgrading={confirmingPurchase}
            />
          )}

          {/* ── Preview Stage ── */}
          {stage === 'preview' && mappingResult && (
            <PreviewStage
              previewWines={previewWines}
              effectiveRowCount={effectiveRowCount}
              confirmedMappings={confirmedMappings}
              profile={profile}
              onImport={handleImport}
              onEditMapping={() => {
                setMergedCSV(null);
                setImportMappings(null);
                setStage('mapping');
              }}
            />
          )}

          {/* ── Importing Stage ── */}
          {stage === 'importing' && (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Spinner size="lg" />
              <Heading scale="subhead">Importing wines...</Heading>
              <Body size="caption" colour="ghost" className="w-auto">
                This may take a minute for large collections.
              </Body>

              {/* Indeterminate progress bar */}
              <div className="w-full max-w-[320px] h-1.5 bg-[var(--rc-surface-secondary)] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--rc-accent-pink)] rounded-full animate-pulse"
                  style={{ width: '60%' }}
                />
              </div>
            </div>
          )}

          {/* ── Complete Stage ── */}
          {stage === 'complete' && (
            <div className="flex flex-col items-center py-12 gap-4 text-center">
              <CheckCircle size={48} className="text-[var(--rc-accent-acid)]" />
              <Heading scale="heading">Import Complete</Heading>

              <div className="space-y-1">
                <Body className="w-auto">
                  <strong>{importProgress.imported}</strong> wine{importProgress.imported !== 1 ? 's' : ''} imported
                </Body>
                {importProgress.duplicates > 0 && (
                  <Body size="caption" colour="secondary" className="w-auto">
                    {importProgress.duplicates} duplicate{importProgress.duplicates !== 1 ? 's' : ''} merged (quantity added)
                  </Body>
                )}
                {importProgress.skipped > 0 && (
                  <Body size="caption" colour="secondary" className="w-auto">
                    {importProgress.skipped} row{importProgress.skipped !== 1 ? 's' : ''} skipped (missing required data)
                  </Body>
                )}
              </div>

              <div className="flex gap-3 mt-4">
                <Button
                  variantType="Primary"
                  label="View Cellar"
                  onClick={() => {
                    onClose();
                    navigate({ to: '/cellar' });
                  }}
                />
                <Button
                  variantType="Secondary"
                  label="Import Another"
                  onClick={() => {
                    setStage('upload');
                    setCsvContent(null);
                    setFileName('');
                    setMappingResult(null);
                    setConfirmedMappings([]);
                    setDuplicateGroups([]);
                    setMergedCSV(null);
                    setImportMappings(null);
                    setParsedWines([]);
                    setFilteredCSV(null);
                    setPassedThroughBottleSelect(false);
                    lastMappingFP.current = '';
                    setError(null);
                    localStorage.removeItem(IMPORT_PENDING_LS_KEY);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── MappingRow ──

function MappingRow({
  mapping,
  usedFields,
  currentField,
  onChange,
  dimmed,
}: {
  mapping: FieldMapping;
  usedFields: Set<string>;
  currentField: ImportableField | null;
  onChange: (field: ImportableField | null) => void;
  dimmed: boolean;
}) {
  const confidenceLabel =
    mapping.confidence === 'high'
      ? 'High confidence match'
      : mapping.confidence === 'medium'
        ? 'Review suggested'
        : 'No match found';

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 py-2 border-b border-[var(--rc-border-subtle)] last:border-0 transition-opacity',
        dimmed && 'opacity-50 hover:opacity-100 focus-within:opacity-100',
      )}
    >
      {/* CSV column */}
      <div className="w-[100px] shrink-0 truncate">
        <MonoLabel size="label" className="text-[11px]">{mapping.csvColumn}</MonoLabel>
      </div>

      {/* Arrow */}
      <ArrowRight size={12} className="text-[var(--rc-ink-ghost)] shrink-0" />

      {/* Target field dropdown */}
      <select
        value={currentField || 'skip'}
        onChange={(e) =>
          onChange(e.target.value === 'skip' ? null : e.target.value as ImportableField)
        }
        className="flex-1 min-w-0 bg-[var(--rc-surface-secondary)] border border-[var(--rc-border-subtle)] rounded-lg px-2 py-1 text-xs text-[var(--rc-ink-primary)]"
      >
        <option value="skip">Skip</option>
        {IMPORTABLE_FIELDS.map((f) => (
          <option
            key={f}
            value={f}
            disabled={usedFields.has(f) && f !== currentField}
          >
            {FIELD_LABELS[f] || f}
          </option>
        ))}
      </select>

      {/* Confidence dot with tooltip */}
      <div
        title={confidenceLabel}
        className={cn(
          'w-2 h-2 rounded-full shrink-0 cursor-help',
          mapping.confidence === 'high'
            ? 'bg-[var(--rc-accent-acid)]'
            : mapping.confidence === 'medium'
              ? 'bg-[var(--rc-accent-coral)]'
              : 'bg-[var(--rc-ink-ghost)]'
        )}
      />

      {/* Sample value (first only) */}
      <div className="w-[110px] shrink-0 truncate hidden sm:block">
        <Body size="caption" colour="ghost" className="w-auto truncate text-[11px]">
          {mapping.sampleValues[0] ?? ''}
        </Body>
      </div>
    </div>
  );
}

// ── Step Progress Indicator ──

const STEP_LABELS = ['Upload', 'Map', 'Duplicates', 'Preview', 'Import'] as const;

function stageToStep(stage: ImportStage): number {
  switch (stage) {
    case 'upload': return 0;
    case 'mapping-loading':
    case 'mapping': return 1;
    case 'dedup-review': return 2;
    case 'bottle-select':
    case 'preview': return 3;
    case 'importing': return 4;
    case 'complete': return 4;
  }
}

function ImportStepIndicator({ stage, hasDuplicates }: { stage: ImportStage; hasDuplicates: boolean }) {
  const currentStep = stageToStep(stage);

  return (
    <div className="flex items-center gap-1.5 mb-5">
      {STEP_LABELS.map((label, i) => {
        // Hide "Duplicates" step if no duplicates
        if (i === 2 && !hasDuplicates) return null;
        const isActive = i === currentStep;
        const isDone = i < currentStep;
        return (
          <React.Fragment key={label}>
            {i > 0 && !(i === 2 && !hasDuplicates) && (
              <div
                className={cn(
                  'flex-1 h-px',
                  isDone ? 'bg-[var(--rc-accent-pink)]' : 'bg-[var(--rc-border-subtle)]',
                )}
              />
            )}
            <div className="flex flex-col items-center gap-0.5">
              <div
                className={cn(
                  'w-2 h-2 rounded-full transition-colors',
                  isActive
                    ? 'bg-[var(--rc-accent-pink)]'
                    : isDone
                      ? 'bg-[var(--rc-accent-pink)]'
                      : 'bg-[var(--rc-border-subtle)]',
                )}
              />
              <MonoLabel
                size="micro"
                colour={isActive || isDone ? undefined : 'ghost'}
                className={cn('w-auto', isActive && 'text-[var(--rc-accent-pink)]')}
              >
                {label}
              </MonoLabel>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Preview Stage ──

const VALID_WINE_TYPES = new Set(['red', 'white', 'rose', 'sparkling', 'dessert', 'orange']);

function PreviewStage({
  previewWines,
  effectiveRowCount,
  confirmedMappings,
  profile,
  onImport,
  onEditMapping,
}: {
  previewWines: Record<string, string>[];
  effectiveRowCount: number;
  confirmedMappings: FieldMapping[];
  profile: { currency: string };
  onImport: () => void;
  onEditMapping: () => void;
}) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const mappedCount = confirmedMappings.filter(m => m.raveCaveField).length;
  const skippedCount = confirmedMappings.filter(m => !m.raveCaveField).length;

  return (
    <div>
      {/* Header with counts */}
      <Body className="w-auto mb-1">
        Showing {Math.min(previewWines.length, 5)} of {effectiveRowCount} wines &middot; {mappedCount} fields mapped
      </Body>
      <Body size="caption" colour="secondary" className="w-auto mb-4">
        Here's how your first wines will look in your cellar.
      </Body>

      {/* Skipped-to-notes reminder */}
      {skippedCount > 0 && (
        <div className="mb-4">
          <InlineMessage
            tone="info"
            message={`${skippedCount} skipped column${skippedCount !== 1 ? 's' : ''} saved to Personal Notes`}
          />
        </div>
      )}

      <div className="space-y-3 mb-6">
        {previewWines.map((wine, i) => {
          const isExpanded = expandedIndex === i;
          const wineType = wine.type?.toLowerCase();
          const hasValidType = wineType && VALID_WINE_TYPES.has(wineType);

          return (
            <Card
              key={i}
              elevation="flat"
              padding="standard"
              className="cursor-pointer"
              onClick={() => setExpandedIndex(isExpanded ? null : i)}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {/* Wine type dot */}
                  {hasValidType && (
                    <WineTypeIndicator
                      wineType={wineType as WineType}
                      format="dot"
                    />
                  )}
                  <Heading scale="subhead" className="truncate">
                    {wine.producer && wine.name
                      ? `${wine.producer} ${wine.name}`
                      : wine.name || wine.producer || 'Unknown Wine'}
                  </Heading>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {wine.vintage && wine.vintage !== '0' && (
                    <MonoLabel size="label" colour="ghost" className="w-auto">
                      {wine.vintage}
                    </MonoLabel>
                  )}
                  {wine.quantity && (
                    <MonoLabel size="label" colour="ghost" className="w-auto">
                      &times;{wine.quantity}
                    </MonoLabel>
                  )}
                  <ChevronDown
                    size={14}
                    className={cn(
                      'text-[var(--rc-ink-ghost)] transition-transform duration-200',
                      isExpanded && 'rotate-180',
                    )}
                  />
                </div>
              </div>
              <Body size="caption" colour="secondary" className="w-auto mt-1">
                {[wine.cepage, wine.region, wine.country]
                  .filter(Boolean)
                  .join(' \u00B7 ')}
              </Body>
              {(wine.price || wine.drinkFrom || wine.drinkUntil) && (
                <Body size="caption" colour="ghost" className="w-auto mt-0.5">
                  {[
                    wine.price ? `${getCurrencySymbol(wine.priceCurrency || profile.currency).trim()}${wine.price}` : null,
                    wine.drinkFrom && wine.drinkUntil
                      ? `Drink ${wine.drinkFrom}\u2013${wine.drinkUntil}`
                      : wine.drinkUntil
                        ? `Drink by ${wine.drinkUntil}`
                        : null,
                  ].filter(Boolean).join(' \u00B7 ')}
                </Body>
              )}

              {/* Expanded detail — all mapped fields */}
              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-[var(--rc-border-subtle)] grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  {confirmedMappings
                    .filter(m => m.raveCaveField && wine[m.raveCaveField])
                    .map(m => (
                      <React.Fragment key={m.raveCaveField}>
                        <MonoLabel size="micro" colour="ghost" className="w-auto">
                          {FIELD_LABELS[m.raveCaveField!] || m.raveCaveField}
                        </MonoLabel>
                        <Body size="caption" className="w-auto truncate">
                          {wine[m.raveCaveField!]}
                        </Body>
                      </React.Fragment>
                    ))}
                </div>
              )}
            </Card>
          );
        })}

        {previewWines.length === 0 && (
          <Body size="caption" colour="ghost" className="w-auto text-center py-8">
            No preview available — check your column mapping
          </Body>
        )}
      </div>

      {/* Edit mapping link */}
      <div className="text-center mb-4">
        <button
          onClick={(e) => { e.preventDefault(); onEditMapping(); }}
          className="font-[var(--rc-font-mono)] text-[11px] uppercase tracking-wider text-[var(--rc-ink-ghost)] hover:text-[var(--rc-accent-pink)] transition-colors"
        >
          Something look wrong? Edit column mapping
        </button>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-3">
        <Button
          variantType="Primary"
          label={`Import ${effectiveRowCount} Wine${effectiveRowCount === 1 ? '' : 's'}`}
          onClick={onImport}
          disabled={effectiveRowCount === 0}
        />
      </div>
    </div>
  );
}

// ── CSV line parser (handles quoted fields) ──

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if ((char === ',' || char === '\t') && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current);
  return result;
}
